/**
 * AdminDashboard.jsx — Tableau de bord administrateur
 *
 * Accessible uniquement aux comptes avec role === 'admin'.
 * Sections disponibles :
 *  - overview         : résumé de l'activité (compteurs, mini-cartes)
 *  - agencyValidation : valider ou refuser les dossiers d'inscription agence
 *  - photoModeration  : approuver ou refuser les photos de profil en attente
 *  - complaints       : gérer les réclamations clients
 *  - messages         : consulter toutes les conversations du site
 *  - products         : modifier/supprimer les annonces véhicules
 *  - blogs            : modifier/supprimer les articles
 *  - reservations     : changer le statut des réservations
 *  - users            : gérer les comptes (activer, réinitialiser mdp, supprimer)
 *
 * Toutes les actions utilisent fetchJson(), un wrapper autour de fetch()
 * qui gère les erreurs et parse le JSON automatiquement.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
	FiAlertTriangle,
	FiBarChart2,
	FiCalendar,
	FiCheckCircle,
	FiEdit3,
	FiEye,
	FiEyeOff,
	FiFileText,
	FiHome,
	FiKey,
	FiMessageSquare,
	FiPackage,
	FiRefreshCw,
	FiShield,
	FiTrash2,
	FiUserCheck,
	FiUsers,
	FiXCircle
} from 'react-icons/fi';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import './AdminDashboard.css';

const apiBaseUrl = getApiBaseUrl();

// Navigation latérale du dashboard admin
const sections = [
	{ id: 'overview', label: 'Synthese', icon: FiBarChart2 },
	{ id: 'agencyValidation', label: 'Validation agences', icon: FiUsers },
	{ id: 'photoModeration', label: 'Photos profils', icon: FiEye },
	{ id: 'complaints', label: 'Reclamations', icon: FiAlertTriangle },
	{ id: 'messages', label: 'Messages', icon: FiMessageSquare },
	{ id: 'products', label: 'Annonces', icon: FiPackage },
	{ id: 'blogs', label: 'Contenus', icon: FiFileText },
	{ id: 'reservations', label: 'Reservations', icon: FiCalendar },
	{ id: 'users', label: 'Comptes', icon: FiShield }
];

function formatFileSize(size) {
	const value = Number(size || 0);
	if (!value) {
		return '0 Ko';
	}

	if (value < 1024) {
		return `${value} o`;
	}

	if (value < 1024 * 1024) {
		return `${(value / 1024).toFixed(1)} Ko`;
	}

	return `${(value / (1024 * 1024)).toFixed(1)} Mo`;
}

function isImageMimeType(mimeType) {
	return String(mimeType || '').toLowerCase().startsWith('image/');
}

function formatDate(value) {
	if (!value) {
		return '-';
	}

	return new Date(value).toLocaleString('fr-FR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit'
	});
}

function statusLabel(status) {
	const normalized = String(status || '').toLowerCase();
	if (normalized === 'approved' || normalized === 'confirmed' || normalized === 'resolved') {
		return 'is-ok';
	}
	if (normalized === 'pending' || normalized === 'open' || normalized === 'in_progress') {
		return 'is-pending';
	}
	return 'is-danger';
}

function AdminDashboard() {
	const navigate = useNavigate();
	const [authUser, setAuthUser] = useState(null);
	const [activeSection, setActiveSection] = useState('overview');
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [summary, setSummary] = useState(null);
	const [agencies, setAgencies] = useState([]);
	const [complaints, setComplaints] = useState([]);
	const [discussions, setDiscussions] = useState([]);
	const [selectedDiscussion, setSelectedDiscussion] = useState(null);
	const [products, setProducts] = useState([]);
	const [blogs, setBlogs] = useState([]);
	const [reservations, setReservations] = useState([]);
	const [users, setUsers] = useState([]);
	const [temporaryPasswords, setTemporaryPasswords] = useState({});
	const [userRoleFilter, setUserRoleFilter] = useState('all');
	const [photoRejectReasons, setPhotoRejectReasons] = useState({});
	const [agencyRejectReasons, setAgencyRejectReasons] = useState({});
	const [adminProfileLoaded, setAdminProfileLoaded] = useState(false);

	useEffect(() => {
		const savedUser = window.localStorage.getItem('exp-auth-user');
		if (!savedUser) {
			navigate('/login');
			return;
		}

		const parsedUser = JSON.parse(savedUser);
		if (parsedUser.role !== 'admin') {
			navigate('/');
			return;
		}

		setAuthUser(parsedUser);
	}, [navigate]);

	/**
	 * fetchJson — Wrapper fetch centralisé pour le dashboard admin.
	 * Ajoute Content-Type JSON automatiquement et lance une erreur si la réponse n'est pas OK.
	 */
	const fetchJson = useCallback(async (path, options = {}) => {
		const response = await fetch(`${apiBaseUrl}${path}`, {
			...options,
			headers: {
				'Content-Type': 'application/json',
				...(options.headers || {})
			}
		});

		const contentType = response.headers.get('content-type') || '';
		const data = contentType.includes('application/json') ? await response.json() : null;

		if (!response.ok) {
			throw new Error(data?.message || 'Requete admin echouee.');
		}

		return data;
	}, []);

	/**
	 * loadData — Charge toutes les données admin en parallèle (Promise.all).
	 * Alimente les 8 états locaux d'un seul coup pour minimiser les re-rendus.
	 */
	const loadData = useCallback(async () => {
		setLoading(true);
		setError('');

		try {
			const [dashboardData, agencyData, complaintData, productData, blogData, reservationData, userData, discussionData] = await Promise.all([
				fetchJson('/api/admin/dashboard'),
				fetchJson('/api/admin/agencies/requests'),
				fetchJson('/api/complaints'),
				fetchJson('/api/products'),
				fetchJson('/api/blogs'),
				fetchJson('/api/reservations'),
				fetchJson('/api/admin/users'),
				fetchJson('/api/discussions?all=1')
			]);
			setSummary(dashboardData);
			setAgencies(agencyData);
			setComplaints(complaintData);
			setProducts(productData);
			setBlogs(blogData);
			setReservations(reservationData);
			setUsers(userData);
			setDiscussions(Array.isArray(discussionData) ? discussionData : []);
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setLoading(false);
		}
	}, [fetchJson]);

	useEffect(() => {
		const loadAdminProfile = async () => {
			if (!authUser?.id || adminProfileLoaded) {
				return;
			}

			try {
				const response = await fetch(`${apiBaseUrl}/api/users/${authUser.id}`);
				const data = await response.json();

				if (response.ok && data?.user) {
					setAuthUser((currentUser) => {
						const mergedUser = { ...currentUser, ...data.user };
						window.localStorage.setItem('exp-auth-user', JSON.stringify(mergedUser));
						return mergedUser;
					});
				}
			} catch (fetchError) {
				console.error('Impossible de charger le profil admin.', fetchError);
			} finally {
				setAdminProfileLoaded(true);
			}
		};

		loadAdminProfile();
	}, [authUser?.id, adminProfileLoaded]);

	useEffect(() => {
		if (!authUser) {
			return;
		}

		loadData();
	}, [authUser, loadData]);

	// Cartes de synthèse (compteurs affichés en haut du dashboard)
	const summaryCards = useMemo(
		() => [
			{ label: 'Comptes', value: summary?.users ?? 0 },
			{ label: 'Agences en attente', value: summary?.pendingAgencies ?? 0 },
			{ label: 'Photos en attente', value: users.filter((user) => user.photoModeration?.status === 'pending').length },
			{ label: 'Annonces', value: summary?.products ?? 0 },
			{ label: 'Reclamations', value: summary?.complaints ?? 0 },
			{ label: 'Contenus', value: summary?.blogs ?? 0 },
			{ label: 'Reservations', value: summary?.reservations ?? 0 }
		],
		[summary, users]
	);


	// Agences en attente de validation (statut === 'pending')
	const pendingAgencySubmissions = useMemo(
		() =>
			agencies.filter((agency) => {
				const status = agency.agencyProfile?.submissionStatus || agency.accountStatus;
				return status === 'pending';
			}),
		[agencies]
	);

	// Utilisateurs ayant soumis une photo de profil en attente de modération
	const pendingPhotoUsers = useMemo(
		() => users.filter((user) => user.photoModeration?.status === 'pending' && user.photoModeration?.pendingPhotoUrl),
		[users]
	);

	const userRoleCounts = useMemo(() => {
		const counts = { all: users.length, user: 0, agency: 0, admin: 0 };

		for (const user of users) {
			if (counts[user.role] !== undefined) {
				counts[user.role] += 1;
			}
		}

		return counts;
	}, [users]);

	const filteredUsers = useMemo(() => {
		if (userRoleFilter === 'all') {
			return users;
		}

		return users.filter((user) => user.role === userRoleFilter);
	}, [users, userRoleFilter]);

	const authDisplayName = useMemo(() => {
		if (!authUser) {
			return '';
		}

		const fullName = `${authUser.firstName || ''} ${authUser.lastName || ''}`.trim();
		return fullName || authUser.username || authUser.email || 'Admin';
	}, [authUser]);

	const authAvatarFallback = useMemo(() => {
		if (!authDisplayName) {
			return 'AD';
		}

		return authDisplayName
			.split(' ')
			.filter(Boolean)
			.slice(0, 2)
			.map((part) => part[0]?.toUpperCase() || '')
			.join('')
			.slice(0, 2);
	}, [authDisplayName]);

	const resolveAssetUrl = useCallback((value) => {
		const normalized = String(value || '').trim();
		if (!normalized) {
			return '';
		}

		if (/^https?:\/\//i.test(normalized)) {
			return normalized;
		}

		if (normalized.startsWith('/')) {
			return `${apiBaseUrl}${normalized}`;
		}

		return `${apiBaseUrl}/${normalized}`;
	}, []);

	const handleAgencyDecision = async (agencyId, action) => {
		setSaving(true);
		setError('');
		try {
			const reason = String(agencyRejectReasons[agencyId] || '').trim();
			if (action === 'reject' && !reason) {
				setError('Merci de renseigner la raison du refus du dossier agence.');
				return;
			}

			await fetchJson(`/api/admin/agencies/${agencyId}/submission-review`, {
				method: 'PATCH',
				body: JSON.stringify({
					decision: action,
					reason
				})
			});

			setAgencyRejectReasons((previous) => ({ ...previous, [agencyId]: '' }));
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleComplaintUpdate = async (complaintId, payload) => {
		setSaving(true);
		setError('');
		try {
			await fetchJson(`/api/complaints/${complaintId}`, {
				method: 'PATCH',
				body: JSON.stringify(payload)
			});
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async (path) => {
		setSaving(true);
		setError('');
		try {
			await fetchJson(path, { method: 'DELETE' });
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleSaveProduct = async (product) => {
		setSaving(true);
		setError('');
		try {
			// Retrouve l'ID de l'agence à partir du nom (l'admin peut changer le nom de l'agence d'une annonce)
			const matchingAgency = agencies.find(
				(agency) =>
					String(agency.agencyName || '').trim().toLowerCase() === String(product.agencyName || '').trim().toLowerCase()
			);

			await fetchJson(`/api/products/${product._id || product.id}`, {
				method: 'PATCH',
				body: JSON.stringify({
					name: product.name,
					description: product.description,
					price: Number(product.price),
					stock: Number(product.stock),
					agencyName: product.agencyName || '',
					agencyId: matchingAgency ? matchingAgency.id : null
				})
			});
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleSaveBlog = async (blog) => {
		setSaving(true);
		setError('');
		try {
			await fetchJson(`/api/blogs/${blog._id || blog.id}`, {
				method: 'PATCH',
				body: JSON.stringify({
					title: blog.title,
					content: blog.content,
					author: blog.author
				})
			});
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleSaveReservation = async (reservation) => {
		setSaving(true);
		setError('');
		try {
			await fetchJson(`/api/reservations/${reservation._id || reservation.id}`, {
				method: 'PATCH',
				body: JSON.stringify({
					status: reservation.status
				})
			});
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleToggleUserStatus = async (user) => {
		setSaving(true);
		setError('');
		try {
			await fetchJson(`/api/admin/users/${user.id}/status`, {
				method: 'PATCH',
				body: JSON.stringify({ isActive: !user.isActive })
			});
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleResetUserPassword = async (user) => {
		setSaving(true);
		setError('');
		try {
			const data = await fetchJson(`/api/admin/users/${user.id}/reset-password`, {
				method: 'PATCH'
			});
			// Le mot de passe temporaire est affiché une fois à l'admin, masqué par défaut
			setTemporaryPasswords((previous) => ({
				...previous,
				[user.id]: {
					value: data.temporaryPassword,
					visible: false
				}
			}));
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const handleReviewUserPhoto = async (user, decision) => {
		setSaving(true);
		setError('');
		try {
			const reason = String(photoRejectReasons[user.id] || '').trim();
			if (decision === 'reject' && !reason) {
				setError('Merci de renseigner la cause du refus de la photo.');
				return;
			}

			await fetchJson(`/api/admin/users/${user.id}/photo-review`, {
				method: 'PATCH',
				body: JSON.stringify({ decision, reason })
			});

			setPhotoRejectReasons((previous) => ({ ...previous, [user.id]: '' }));
			await loadData();
		} catch (loadError) {
			setError(loadError.message);
		} finally {
			setSaving(false);
		}
	};

	const togglePasswordVisibility = (userId) => {
		setTemporaryPasswords((previous) => {
			if (!previous[userId]) {
				return previous;
			}

			return {
				...previous,
				[userId]: {
					...previous[userId],
					visible: !previous[userId].visible
				}
			};
		});
	};

	if (!authUser) {
		return null;
	}

	return (
		<div className="ux-admin-page">
			<aside className="ux-admin-sidebar">
				<Link to="/" className="ux-admin-brand">
					<span className="ux-admin-brand-mark">
						<img src="/assets/images/logo.png" alt="" aria-hidden="true" className="ux-admin-brand-logo" />
					</span>
					<div>
						<strong>UltimateX Admin</strong>
						<p>Centre de controle</p>
					</div>
				</Link>

				<nav className="ux-admin-nav" aria-label="Navigation admin">
					{sections.map((section) => {
						const SectionIcon = section.icon;
						return (
							<button
								key={section.id}
								type="button"
								className={`ux-admin-nav-item ${activeSection === section.id ? 'is-active' : ''}`}
								onClick={() => setActiveSection(section.id)}
							>
								<SectionIcon size={16} />
								<span>{section.label}</span>
							</button>
						);
					})}
				</nav>

				<div className="ux-admin-sidebar-footer">
					<div className="ux-admin-user-chip">
						<span className="ux-admin-user-avatar" aria-hidden="true">
							{authUser?.profilePhotoUrl ? (
								<img src={authUser.profilePhotoUrl} alt="" className="ux-admin-user-avatar-image" />
							) : (
								<span className="ux-admin-user-avatar-fallback">{authAvatarFallback}</span>
							)}
						</span>
						<div>
							<span>Connecte en tant que : </span>
							<strong>{authDisplayName}</strong>
						</div>
					</div>
					<p>Role: {authUser.role}</p>
				</div>
			</aside>

			<main className="ux-admin-main">
				<header className="ux-admin-header">
					<div>
						<p className="ux-admin-kicker">Dashboard administration</p>
						<h1>Gestion complete du site</h1>
						<p>
							Valide les dossiers agences (avec documents et photos), modere les photos profils et pilote toutes les operations.
						</p>
					</div>

					<div className="ux-admin-header-actions">
						<Link to="/" className="ux-admin-home-link">
							<FiHome size={16} />
							Retour a l'accueil
						</Link>
						<button type="button" className="ux-admin-refresh" onClick={loadData} disabled={loading || saving}>
							<FiRefreshCw size={16} />
							Rafraichir
						</button>
					</div>
				</header>

				{error ? <div className="ux-admin-alert">{error}</div> : null}

				<section className="ux-admin-summary-grid">
					{summaryCards.map((card) => (
						<article key={card.label} className="ux-admin-summary-card">
							<span>{card.label}</span>
							<strong>{loading ? '...' : card.value}</strong>
						</article>
					))}
				</section>

				{activeSection === 'overview' ? (
					<section className="ux-admin-panel">
						<h2>Activite recente</h2>
						<div className="ux-admin-overview-grid">
							<div className="ux-admin-mini-card">
								<h3>Agences en attente</h3>
								<p>{pendingAgencySubmissions.length} dossier(s) a traiter</p>
							</div>
							<div className="ux-admin-mini-card">
								<h3>Photos profils</h3>
								<p>{pendingPhotoUsers.length} validation(s) en attente</p>
							</div>
							<div className="ux-admin-mini-card">
								<h3>Reclamations</h3>
								<p>{complaints.length} dossier(s) visible(s)</p>
							</div>
							<div className="ux-admin-mini-card">
								<h3>Annonces</h3>
								<p>{products.length} annonce(s) sur le site</p>
							</div>
						</div>
					</section>
				) : null}

				{activeSection === 'agencyValidation' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Validation des inscriptions agences</h2>
							<span>{pendingAgencySubmissions.length} dossier(s) en attente</span>
						</div>
						<div className="ux-admin-stack">
							{agencies.map((agency) => (
								<article key={agency.id} className="ux-admin-card">
									<div>
										<div className="ux-admin-card-topline">
											<h3>{agency.agencyProfile?.agencyName || `${agency.firstName} ${agency.lastName}`}</h3>
											<span className={statusLabel(agency.agencyProfile?.submissionStatus || agency.accountStatus)}>
												{agency.agencyProfile?.submissionStatus || agency.accountStatus}
											</span>
										</div>
										<p><strong>Responsable:</strong> {agency.firstName} {agency.lastName}</p>
										<p><strong>Email:</strong> {agency.email}</p>
										<p><strong>Telephone:</strong> {agency.agencyProfile?.phoneNumber || '-'}</p>
										<p><strong>Adresse:</strong> {agency.agencyProfile?.agencyAddress || '-'}</p>
										<p><strong>Site web:</strong> {agency.agencyProfile?.website || '-'}</p>
										<p>
											<strong>Etat compte:</strong> {agency.accountStatus} - {agency.isActive ? 'actif' : 'inactif'}
										</p>
										<p><strong>Annonces liees:</strong> {agency.listingCount || 0}</p>
										<p><strong>Soumis le:</strong> {formatDate(agency.agencyProfile?.submittedAt || agency.createdAt)}</p>
										{agency.agencyProfile?.reviewedAt ? (
											<p><strong>Revise le:</strong> {formatDate(agency.agencyProfile.reviewedAt)}</p>
										) : null}
										{agency.agencyProfile?.submissionReviewNote ? (
											<p className="ux-admin-message"><strong>Motif refus:</strong> {agency.agencyProfile.submissionReviewNote}</p>
										) : null}

										<div className="ux-admin-doc-grid">
											{[
												{ key: 'rcDocument', label: 'Registre de commerce (RC)' },
												{ key: 'nifDocument', label: 'NIF' },
												{ key: 'ownerIdentityDocument', label: 'Piece identite proprietaire' },
												{ key: 'agencyFrontPhoto', label: 'Photo facade agence' },
												{ key: 'ownerLivePhoto', label: 'Photo responsable sur place' },
												{ key: 'agencyLogo', label: 'Logo agence (optionnel)' }
											].map((documentConfig) => {
												const document = agency.agencyProfile?.submissionDocuments?.[documentConfig.key] || {};
												const assetUrl = resolveAssetUrl(document.url);
												const hasDocument = Boolean(assetUrl);

												return (
													<div key={documentConfig.key} className="ux-admin-doc-card">
														<p className="ux-admin-doc-title">{documentConfig.label}</p>
														{hasDocument && isImageMimeType(document.mimeType) ? (
															<img
																src={assetUrl}
																alt={documentConfig.label}
																className="ux-admin-doc-preview"
															/>
														) : null}
														{hasDocument ? (
															<>
																<p className="ux-admin-doc-meta">{document.originalName || 'Document'}</p>
																<p className="ux-admin-doc-meta">{document.mimeType || 'Type inconnu'} - {formatFileSize(document.size)}</p>
																<a className="ux-admin-doc-link" href={assetUrl} target="_blank" rel="noreferrer">
																	Voir le fichier
																</a>
															</>
														) : (
															<p className="ux-admin-empty-inline">Non fourni</p>
														)}
													</div>
												);
											})}
										</div>

										{agency.listings?.length ? (
											<div className="ux-admin-agency-listings">
												{agency.listings.map((listing) => (
													<div key={listing.id} className="ux-admin-agency-listing">
														<strong>{listing.name}</strong>
														<span>{listing.price} DH</span>
														<span>Stock: {listing.stock}</span>
													</div>
												))}
											</div>
										) : (
											<p className="ux-admin-empty-inline">Aucune annonce liee pour le moment.</p>
										)}
									</div>
									<div className="ux-admin-card-actions">
										{(agency.agencyProfile?.submissionStatus || agency.accountStatus) === 'pending' ? (
											<>
												<button type="button" className="ux-admin-action is-success" onClick={() => handleAgencyDecision(agency.id, 'approve')} disabled={saving}>
													<FiCheckCircle size={16} />
													Valider
												</button>
												<label className="ux-admin-inline-field">
													<span>Raison du refus (obligatoire)</span>
													<input
														type="text"
														value={agencyRejectReasons[agency.id] || ''}
														onChange={(event) =>
															setAgencyRejectReasons((previous) => ({
																...previous,
																[agency.id]: event.target.value
															}))
														}
														placeholder="Ex: documents illegibles"
													/>
												</label>
												<button type="button" className="ux-admin-action is-danger" onClick={() => handleAgencyDecision(agency.id, 'reject')} disabled={saving}>
													<FiXCircle size={16} />
													Refuser
												</button>
											</>
										) : null}
									</div>
								</article>
							))}
						</div>
					</section>
				) : null}

				{activeSection === 'photoModeration' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Moderation des photos de profil</h2>
							<span>{pendingPhotoUsers.length} photo(s) en attente</span>
						</div>
						<div className="ux-admin-grid">
							{pendingPhotoUsers.map((user) => (
								<article key={user.id} className="ux-admin-card ux-admin-card-vertical">
									<div className="ux-admin-photo-review">
										<p className="ux-admin-photo-review-title">Validation photo de profil</p>
										<div className="ux-admin-photo-review-row">
											<img
												src={user.photoModeration.pendingPhotoUrl}
												alt={`${user.firstName || 'Utilisateur'} ${user.lastName || ''}`.trim()}
												className="ux-admin-photo-review-preview"
											/>
											<label className="ux-admin-photo-review-field">
												<span>Cause du refus (obligatoire si refus)</span>
												<input
													type="text"
													value={photoRejectReasons[user.id] || ''}
													onChange={(event) =>
														setPhotoRejectReasons((previous) => ({
															...previous,
															[user.id]: event.target.value
														}))
													}
													placeholder="Ex: photo floue / visage non visible"
												/>
											</label>
										</div>
										<p>
											<strong>Utilisateur:</strong> {user.firstName} {user.lastName} ({user.email})
										</p>
									</div>
									<div className="ux-admin-card-actions">
										<button type="button" className="ux-admin-action is-success" onClick={() => handleReviewUserPhoto(user, 'approve')} disabled={saving}>
											<FiCheckCircle size={16} />
											Approuver photo
										</button>
										<button type="button" className="ux-admin-action is-danger" onClick={() => handleReviewUserPhoto(user, 'reject')} disabled={saving}>
											<FiXCircle size={16} />
											Refuser photo
										</button>
									</div>
								</article>
							))}
						</div>
					</section>
				) : null}

				{activeSection === 'complaints' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Reclamations clients</h2>
							<span>{complaints.length} plainte(s)</span>
						</div>
						<div className="ux-admin-grid">
							{complaints.map((complaint) => {
								const cid = complaint._id || complaint.id;
								// Gestion des prises en charge : un admin à la fois peut gérer une réclamation
								const isAssignedToMe = complaint.assignedTo === authDisplayName;
								const isAssignedToOther = complaint.assignedTo && !isAssignedToMe;
								return (
									<article key={cid} className="ux-admin-card ux-admin-card-vertical">
										<div className="ux-admin-card-topline">
											<strong>{complaint.subject}</strong>
											<span className={statusLabel(complaint.status)}>{complaint.status}</span>
										</div>
										<p>{complaint.fullName} — {complaint.email}</p>
										<p className="ux-admin-message">{complaint.message}</p>

										{/* Bandeau prise en charge */}
										{isAssignedToMe && (
											<div className="ux-admin-assigned-me">
												<FiUserCheck size={14} />
												Vous gérez cette réclamation
												<button
													type="button"
													className="ux-admin-release-btn"
													onClick={() => handleComplaintUpdate(cid, { assignedTo: '', assignedAt: null, status: complaint.status })}
													disabled={saving}
												>
													Libérer
												</button>
											</div>
										)}
										{isAssignedToOther && (
											<div className="ux-admin-assigned-other">
												<FiUserCheck size={14} />
												Pris en charge par <strong>{complaint.assignedTo}</strong>
											</div>
										)}
										{!complaint.assignedTo && (
											<button
												type="button"
												className="ux-admin-take-btn"
												onClick={() => handleComplaintUpdate(cid, { assignedTo: authDisplayName, assignedAt: new Date().toISOString(), status: complaint.status === 'open' ? 'in_progress' : complaint.status })}
												disabled={saving}
											>
												<FiUserCheck size={14} /> Prendre en charge
											</button>
										)}

										<div className="ux-admin-form-row">
											<label>
												<span>Statut</span>
												<select value={complaint.status} onChange={(e) => handleComplaintUpdate(cid, { status: e.target.value })} disabled={isAssignedToOther}>
													<option value="open">Ouverte</option>
													<option value="in_progress">En cours</option>
													<option value="resolved">Résolue</option>
												</select>
											</label>
											<label>
												<span>Priorité</span>
												<select value={complaint.priority} onChange={(e) => handleComplaintUpdate(cid, { priority: e.target.value })} disabled={isAssignedToOther}>
													<option value="low">Basse</option>
													<option value="medium">Moyenne</option>
													<option value="high">Haute</option>
												</select>
											</label>
										</div>
										<button type="button" className="ux-admin-delete" onClick={() => handleDelete(`/api/complaints/${cid}`)} disabled={saving || isAssignedToOther}>
											<FiTrash2 size={16} /> Supprimer
										</button>
									</article>
								);
							})}
						</div>
					</section>
				) : null}

				{activeSection === 'messages' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Messages du site</h2>
							<span>{discussions.length} discussion(s)</span>
						</div>
						{selectedDiscussion ? (
							<div className="ux-admin-discussion-view">
								<button type="button" className="ux-admin-back-btn" onClick={() => setSelectedDiscussion(null)}>
									← Retour à la liste
								</button>
								<div className="ux-admin-discussion-header">
									<div>
										<strong>{selectedDiscussion.vehicleName || 'Annonce'}</strong>
										<span>{selectedDiscussion.agencyName} · {selectedDiscussion.customerName}</span>
									</div>
									<span className="ux-admin-msg-count">{selectedDiscussion.messages?.length || 0} message(s)</span>
								</div>
								<div className="ux-admin-messages-list">
									{(selectedDiscussion.messages || []).map((msg) => (
										<div
											key={msg._id}
											className={`ux-admin-msg ux-admin-msg-${msg.senderRole}`}
										>
											<div className="ux-admin-msg-meta">
												<strong>{msg.senderName}</strong>
												<span className={`ux-admin-msg-role ux-admin-msg-role-${msg.senderRole}`}>{msg.senderRole}</span>
												<time>{msg.createdAt ? new Date(msg.createdAt).toLocaleString('fr-FR') : ''}</time>
											</div>
											<p className="ux-admin-msg-text">{msg.text}</p>
										</div>
									))}
								</div>
							</div>
						) : (
							<div className="ux-admin-stack">
								{discussions.length === 0 && (
									<p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '2rem' }}>Aucune discussion pour le moment.</p>
								)}
								{discussions.map((disc) => (
									<article
										key={disc._id}
										className="ux-admin-card ux-admin-discussion-row"
										onClick={() => setSelectedDiscussion(disc)}
										style={{ cursor: 'pointer' }}
									>
										<div className="ux-admin-discussion-row-left">
											<div className="ux-admin-discussion-icon">
												<FiMessageSquare size={16} />
											</div>
											<div>
												<strong>{disc.vehicleName || 'Annonce sans nom'}</strong>
												<span>Agence : {disc.agencyName || '—'} · Client : {disc.customerName || '—'}</span>
												<p className="ux-admin-discussion-preview">{disc.lastMessagePreview || 'Aucun message'}</p>
											</div>
										</div>
										<div className="ux-admin-discussion-row-right">
											<span className="ux-admin-discussion-count">{disc.messages?.length || 0} msg</span>
											<time>{disc.lastMessageAt ? new Date(disc.lastMessageAt).toLocaleDateString('fr-FR') : ''}</time>
										</div>
									</article>
								))}
							</div>
						)}
					</section>
				) : null}

				{activeSection === 'products' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Annonces produits</h2>
							<span>{products.length} element(s)</span>
						</div>
						<div className="ux-admin-stack">
							{products.map((product, index) => (
								<article key={product._id || product.id || index} className="ux-admin-card ux-admin-card-form">
									<label>
										<span>Nom</span>
										<input value={product.name || ''} onChange={(event) => {
											const updated = [...products];
											updated[index] = { ...updated[index], name: event.target.value };
											setProducts(updated);
										}} />
									</label>
									<label>
										<span>Description</span>
										<textarea value={product.description || ''} onChange={(event) => {
											const updated = [...products];
											updated[index] = { ...updated[index], description: event.target.value };
											setProducts(updated);
										}} />
									</label>
									<div className="ux-admin-form-row">
										<label>
											<span>Prix</span>
											<input type="number" value={product.price ?? ''} onChange={(event) => {
												const updated = [...products];
												updated[index] = { ...updated[index], price: event.target.value };
												setProducts(updated);
											}} />
										</label>
										<label>
											<span>Stock</span>
											<input type="number" value={product.stock ?? ''} onChange={(event) => {
												const updated = [...products];
												updated[index] = { ...updated[index], stock: event.target.value };
												setProducts(updated);
											}} />
										</label>
									</div>
									<label>
										<span>Agence proprietaire</span>
										<input
											list="ux-admin-agency-names"
											value={product.agencyName || ''}
											onChange={(event) => {
												const updated = [...products];
												updated[index] = { ...updated[index], agencyName: event.target.value };
												setProducts(updated);
											}}
											placeholder="Nom agence"
										/>
									</label>
									<div className="ux-admin-card-actions">
										<button type="button" className="ux-admin-action is-primary" onClick={() => handleSaveProduct(product)} disabled={saving}>
											<FiEdit3 size={16} />
											Enregistrer
										</button>
										<button type="button" className="ux-admin-delete" onClick={() => handleDelete(`/api/products/${product._id || product.id}`)} disabled={saving}>
											<FiTrash2 size={16} />
											Supprimer
										</button>
									</div>
								</article>
							))}
						</div>
					</section>
				) : null}

				{activeSection === 'blogs' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Contenus / blogs</h2>
							<span>{blogs.length} article(s)</span>
						</div>
						<div className="ux-admin-stack">
							{blogs.map((blog, index) => (
								<article key={blog._id || blog.id || index} className="ux-admin-card ux-admin-card-form">
									<label>
										<span>Titre</span>
										<input value={blog.title || ''} onChange={(event) => {
											const updated = [...blogs];
											updated[index] = { ...updated[index], title: event.target.value };
											setBlogs(updated);
										}} />
									</label>
									<label>
										<span>Contenu</span>
										<textarea value={blog.content || ''} onChange={(event) => {
											const updated = [...blogs];
											updated[index] = { ...updated[index], content: event.target.value };
											setBlogs(updated);
										}} />
									</label>
									<div className="ux-admin-form-row">
										<label>
											<span>Auteur</span>
											<input value={blog.author || ''} onChange={(event) => {
												const updated = [...blogs];
												updated[index] = { ...updated[index], author: event.target.value };
												setBlogs(updated);
											}} />
										</label>
									</div>
									<div className="ux-admin-card-actions">
										<button type="button" className="ux-admin-action is-primary" onClick={() => handleSaveBlog(blog)} disabled={saving}>
											<FiEdit3 size={16} />
											Enregistrer
										</button>
										<button type="button" className="ux-admin-delete" onClick={() => handleDelete(`/api/blogs/${blog._id || blog.id}`)} disabled={saving}>
											<FiTrash2 size={16} />
											Supprimer
										</button>
									</div>
								</article>
							))}
						</div>
					</section>
				) : null}

				{activeSection === 'reservations' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Reservations</h2>
							<span>{reservations.length} dossier(s)</span>
						</div>
						<div className="ux-admin-stack">
							{reservations.map((reservation) => (
								<article key={reservation._id || reservation.id} className="ux-admin-card ux-admin-card-vertical">
									<div>
										<h3>{reservation.fullName}</h3>
										<p>{reservation.vehicleName}</p>
										<p>{formatDate(reservation.startDate)} - {formatDate(reservation.endDate)}</p>
									</div>
									<div className="ux-admin-form-row">
										<label>
											<span>Status</span>
											<select value={reservation.status} onChange={(event) => handleSaveReservation({ ...reservation, status: event.target.value })}>
												<option value="pending">En attente</option>
												<option value="confirmed">Confirmee</option>
												<option value="cancelled">Annulee</option>
											</select>
										</label>
									</div>
									<button type="button" className="ux-admin-delete" onClick={() => handleDelete(`/api/reservations/${reservation._id || reservation.id}`)} disabled={saving}>
										<FiTrash2 size={16} />
										Supprimer
									</button>
								</article>
							))}
						</div>
					</section>
				) : null}

				{activeSection === 'users' ? (
					<section className="ux-admin-panel">
						<div className="ux-admin-panel-head">
							<h2>Comptes utilisateurs</h2>
							<span>{filteredUsers.length} compte(s)</span>
						</div>
						<div className="ux-admin-role-filters" role="group" aria-label="Filtrer les comptes">
							<button
								type="button"
								className={`ux-admin-role-filter ${userRoleFilter === 'all' ? 'is-active' : ''}`}
								onClick={() => setUserRoleFilter('all')}
							>
								Tous ({userRoleCounts.all})
							</button>
							<button
								type="button"
								className={`ux-admin-role-filter ${userRoleFilter === 'user' ? 'is-active' : ''}`}
								onClick={() => setUserRoleFilter('user')}
							>
								Users ({userRoleCounts.user})
							</button>
							<button
								type="button"
								className={`ux-admin-role-filter ${userRoleFilter === 'agency' ? 'is-active' : ''}`}
								onClick={() => setUserRoleFilter('agency')}
							>
								Agencies ({userRoleCounts.agency})
							</button>
							<button
								type="button"
								className={`ux-admin-role-filter ${userRoleFilter === 'admin' ? 'is-active' : ''}`}
								onClick={() => setUserRoleFilter('admin')}
							>
								Admins ({userRoleCounts.admin})
							</button>
						</div>
						<div className="ux-admin-grid">
							{filteredUsers.map((user) => (
								<article key={user.id} className="ux-admin-card ux-admin-card-vertical">
									{temporaryPasswords[user.id] ? (
										<div className="ux-admin-password-block">
											<p className="ux-admin-password-title">Mot de passe temporaire</p>
											<div className="ux-admin-password-row">
												<span className="ux-admin-password-value">
													{temporaryPasswords[user.id].visible ? temporaryPasswords[user.id].value : '••••••••••••'}
												</span>
												<button type="button" className="ux-admin-action is-secondary" onClick={() => togglePasswordVisibility(user.id)}>
													{temporaryPasswords[user.id].visible ? <FiEyeOff size={16} /> : <FiEye size={16} />}
													{temporaryPasswords[user.id].visible ? 'Masquer' : 'Afficher'}
												</button>
											</div>
										</div>
									) : null}
									<div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
										<span
											title={user.isActive ? 'Compte actif' : 'Compte inactif'}
											style={{
												marginTop: '0.35rem',
												flexShrink: 0,
												width: 10,
												height: 10,
												borderRadius: '50%',
												background: user.isActive ? '#10b981' : '#ef4444',
												boxShadow: user.isActive
													? '0 0 0 3px rgba(16,185,129,0.2)'
													: '0 0 0 3px rgba(239,68,68,0.2)',
												display: 'inline-block',
											}}
										/>
										<div>
											<h3 style={{ margin: 0 }}>{user.firstName} {user.lastName}</h3>
											<p style={{ margin: '0.15rem 0 0' }}>{user.email}</p>
											<p style={{ margin: '0.1rem 0 0' }}>{user.role} - {user.accountStatus} - {user.isActive ? 'actif' : 'inactif'}</p>
										</div>
									</div>
									<div className="ux-admin-card-actions">
										<button type="button" className="ux-admin-action is-secondary" onClick={() => handleResetUserPassword(user)} disabled={saving}>
											<FiKey size={16} />
											Reinitialiser mdp
										</button>
										<button type="button" className="ux-admin-action is-primary" onClick={() => handleToggleUserStatus(user)} disabled={saving}>
											<FiEdit3 size={16} />
											Basculer actif/inactif
										</button>
										<button type="button" className="ux-admin-delete" onClick={() => handleDelete(`/api/admin/users/${user.id}`)} disabled={saving}>
											<FiTrash2 size={16} />
											Supprimer
										</button>
									</div>
								</article>
							))}
						</div>
					</section>
				) : null}

				<datalist id="ux-admin-agency-names">
					{agencies
						.filter((agency) => Boolean(String(agency.agencyName || '').trim()))
						.map((agency) => (
							<option key={agency.id} value={agency.agencyName} />
						))}
				</datalist>
			</main>
		</div>
	);
}

export default AdminDashboard;